import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
} from '@/components/ui'
import {
  MetricCard,
} from '@/components/snippets'
import {
  Upload,
  FileSearch,
  History,
  TrendingUp,
  Users,
  FileText,
  ArrowRight,
  Loader2,
  AlertCircle,
  RefreshCw,
  Clock,
  Eye,
} from 'lucide-react'
import { statsApi, jobsApi, type DashboardStats } from '@/services/api'

interface RecentJob {
  id: string
  type: string
  customerId: string
  status: string
  createdAt: string
}

export function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([])
  const [isRefreshing, setIsRefreshing] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      const [statsData, jobsData] = await Promise.all([
        statsApi.getDashboardStats(),
        jobsApi.list({ pageSize: 5 })
      ])
      setStats(statsData)
      setRecentJobs(jobsData.jobs as unknown as RecentJob[])
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
      setStats({
        total_analyses: 0,
        active_customers: 0,
        pending_jobs: 0,
        avg_risk_score: 0,
        last_updated: ''
      })
    } finally {
      setStatsLoading(false)
      setIsRefreshing(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
    
    // Auto-refresh every 30 seconds if there are pending jobs
    const interval = setInterval(() => {
      if (stats?.pending_jobs && stats.pending_jobs > 0) {
        fetchData()
      }
    }, 30000)
    
    return () => clearInterval(interval)
  }, [fetchData, stats?.pending_jobs])

  const handleRefresh = () => {
    setIsRefreshing(true)
    fetchData()
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success">Completed</Badge>
      case 'running':
        return <Badge variant="warning">Running</Badge>
      case 'pending':
      case 'queued':
        return <Badge variant="secondary">Pending</Badge>
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-muted-foreground">
            Credit analysis overview and quick actions
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Link to="/upload">
            <Button variant="outline" className="gap-2">
              <Upload className="h-4 w-4" />
              Upload Data
            </Button>
          </Link>
          <Link to="/upload">
            <Button className="gap-2">
              <FileSearch className="h-4 w-4" />
              New Analysis
            </Button>
          </Link>
        </div>
      </div>


      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4">
        {statsLoading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="p-6">
                <div className="flex items-center justify-center h-16">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              </Card>
            ))}
          </>
        ) : (
          <>
            <MetricCard
              title="Total Analyses"
              value={stats?.total_analyses ?? 0}
              format="number"
              icon={FileText}
              color="info"
              trend={stats?.total_analyses ? "up" : undefined}
              trendValue={stats?.total_analyses ? "Lifetime" : undefined}
            />
            <MetricCard
              title="Active Customers"
              value={stats?.active_customers ?? 0}
              format="number"
              icon={Users}
              color="success"
            />
            <MetricCard
              title="Pending Jobs"
              value={stats?.pending_jobs ?? 0}
              format="number"
              icon={History}
              color={stats?.pending_jobs && stats.pending_jobs > 0 ? "warning" : "default"}
            />
            <MetricCard
              title="Avg Risk Score"
              value={stats?.avg_risk_score?.toFixed(1) ?? "N/A"}
              icon={TrendingUp}
              color="default"
            />
          </>
        )}
      </div>

      {/* Getting Started Section */}
      {(!stats || stats.total_analyses === 0) && (
        <Card className="border-dashed border-2 border-gray-300 bg-gray-50/50">
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">No Analyses Yet</h3>
                <p className="text-muted-foreground mt-1 max-w-md mx-auto">
                  Get started by uploading customer data and running your first credit analysis. 
                  The dashboard will populate with insights once you generate a memo.
                </p>
              </div>
              <div className="flex items-center justify-center gap-3 pt-2">
                <Link to="/upload">
                  <Button className="gap-2">
                    <Upload className="h-4 w-4" />
                    Upload & Start Analysis
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Analyses - show if there are any jobs */}
      {recentJobs.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between py-4">
            <CardTitle className="text-lg">Recent Analyses</CardTitle>
            <Link to="/history">
              <Button variant="ghost" size="sm" className="gap-1 text-xs">
                View All <ArrowRight className="h-3 w-3" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {recentJobs.map((job) => (
                <Link 
                  key={job.id} 
                  to={job.status === 'completed' ? `/analysis/${job.id}` : '/history'}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gray-100">
                      <FileText className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{job.customerId}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        {formatDate(job.createdAt)}
                        <span className="capitalize">({job.type})</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(job.status)}
                    {job.status === 'completed' && (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <Link to="/upload">
              <div className="p-4 border rounded-lg hover:bg-accent transition-colors cursor-pointer group">
                <div className="flex items-center justify-between mb-2">
                  <Upload className="h-6 w-6 text-dashboard-teal" />
                  <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-dashboard-teal transition-colors" />
                </div>
                <h4 className="font-medium">Upload & Analyze</h4>
                <p className="text-sm text-muted-foreground">
                  Upload data and run credit analysis
                </p>
              </div>
            </Link>
            <Link to="/history">
              <div className="p-4 border rounded-lg hover:bg-accent transition-colors cursor-pointer group">
                <div className="flex items-center justify-between mb-2">
                  <History className="h-6 w-6 text-dashboard-orange" />
                  <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-dashboard-orange transition-colors" />
                </div>
                <h4 className="font-medium">View History</h4>
                <p className="text-sm text-muted-foreground">
                  Browse past analyses and memos
                </p>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
